import { useContext, useEffect, useState } from "react";

import {
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { getAuthUserMetabaseView } from "@rubi/api-generated";
import { useNavigate } from "react-router";

import { mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useMetabaseEmbedUrl } from "@/hooks/useMetabaseEmbedUrl";
import { IUserPermission } from "@/types";

function AlertAnalytics() {
  const [iframeUrl, setIframeUrl] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const themedIframeUrl = useMetabaseEmbedUrl(iframeUrl);
  const [isReadOnlyDialogOpen, setIsReadOnlyDialogOpen] = useState(false);
  const [selectedGroupID, setSelectedGroupID] = useState(0);
  const navigate = useNavigate();
  const { isDevEnv } = useDevEnvironment();

  const { currentUserGroups, currentUser } = useContext(AuthStateContext);

  const groupsToSelect = currentUserGroups.filter(
    (group) => !group.is_self_group,
  );

  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const readOnly = alertPermissions
    ? !alertPermissions.canAccessDashboard
    : false;

  const getMetabaseURL = (groupID: number) => {
    if (groupID === 0) return;

    setLoadFailed(false);
    getAuthUserMetabaseView({
      query: { group_id: groupID.toString(), dashboard: isDevEnv ? 1 : 3 },
    })
      .then((res) => {
        const url = (res.data as { metabase_url?: string } | undefined)
          ?.metabase_url;
        const isWebUrl = typeof url === "string" && /^https?:\/\//i.test(url);
        setIframeUrl(isWebUrl ? url : "");
        setLoadFailed(!isWebUrl);
      })
      .catch(() => {
        setIframeUrl("");
        setLoadFailed(true);
      });
  };

  const updateSelectedGroup = (groupID: number) => {
    setSelectedGroupID(groupID);
  };

  useEffect(() => {
    if (groupsToSelect.length > 0) {
      setSelectedGroupID(groupsToSelect[0].id);
    }
  }, [currentUserGroups]);

  useEffect(() => {
    if (readOnly) {
      setIsReadOnlyDialogOpen(true);
    }
  }, [readOnly]);

  useEffect(() => {
    if (!readOnly) getMetabaseURL(selectedGroupID);
  }, [selectedGroupID]);

  if (readOnly) {
    return (
      <DisabledComponentDialog
        isDialogOpen={isReadOnlyDialogOpen}
        handleClose={() => {
          navigate(`/rubialert/alerts`);
        }}
        title="You do not have access to view alert analytics."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Alert Analytics",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view alert analytics.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          padding: "8px",
        }}
      >
        <FormControl>
          <InputLabel id="selected-group-label">
            View Dashboard for Group
          </InputLabel>
          <Select
            labelId="selected-group-label"
            id="selected-group"
            value={selectedGroupID}
            label="Selected Group"
            sx={{ minWidth: "300px" }}
            onChange={(event) =>
              updateSelectedGroup(event.target.value as number)
            }
          >
            {groupsToSelect.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      {loadFailed ? (
        <Typography role="alert" color="error" sx={{ padding: "8px" }}>
          The analytics dashboard could not be loaded. Try again later or select
          another group.
        </Typography>
      ) : (
        <iframe
          title="Alert analytics dashboard"
          src={themedIframeUrl}
          style={{
            flexGrow: 1,
            // Drop the browser's default inset frame around the embed.
            border: "none",
          }}
        ></iframe>
      )}
    </div>
  );
}

export default AlertAnalytics;
